/**
 * AppsOverviewPage — The new app-first landing page at /apps.
 *
 * Shows a grid of app cards with per-app counts:
 *   - Logo, name, configKey, tags
 *   - # clients using this app (from credential_vault)
 *   - # scripts (from extension_script)
 *   - latest script update timestamp
 *   - script versions (badges)
 * Filters: text search, category (kind), has-scripts, has-clients
 * Sort:    clients desc / scripts desc / name / last-updated
 *
 * Clicking a card opens /apps/:configKey for the nested scripts view.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Apps as AppsIcon,
  Code as CodeIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import appRegistryService from '../services/api/appRegistryService';

const SORT_OPTIONS = [
  { value: 'default', label: 'Clients ↓ then Scripts ↓' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'clients', label: 'Clients ↓' },
  { value: 'scripts', label: 'Scripts ↓' },
  { value: 'updated', label: 'Last updated' },
];

function formatRelative(iso) {
  if (!iso) return 'never';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  if (diff < 0) return new Date(iso).toLocaleDateString();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return new Date(iso).toLocaleDateString();
}

const AppCard = ({ app, onClick }) => {
  const hasScripts = app.scriptCount > 0;
  const hasClients = app.clientCount > 0;
  return (
    <Card elevation={hasScripts ? 3 : 1} sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: hasScripts || hasClients ? 1 : 0.65 }}>
      <CardActionArea onClick={onClick} sx={{ flexGrow: 1, alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            {app.logo ? (
              <Avatar src={app.logo} alt={app.name} variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'grey.100' }} />
            ) : (
              <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'primary.light' }}>
                <AppsIcon />
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap title={app.displayName || app.name}>
                {app.displayName || app.name}
              </Typography>
              <Typography variant="caption" fontFamily="monospace" color="text.secondary" noWrap>
                {app.configKey}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
            <Tooltip title="Clients using this app (from credential_vault)">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <BusinessIcon fontSize="small" color={hasClients ? 'primary' : 'disabled'} />
                <Typography variant="body2" fontWeight={600}>
                  {app.clientCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">clients</Typography>
              </Stack>
            </Tooltip>
            <Tooltip title="Scripts available for this app">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CodeIcon fontSize="small" color={hasScripts ? 'primary' : 'disabled'} />
                <Typography variant="body2" fontWeight={600}>
                  {app.scriptCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">scripts</Typography>
              </Stack>
            </Tooltip>
          </Stack>

          {app.versions && app.versions.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              {app.versions.slice(0, 4).map((v) => (
                <Tooltip key={v.scriptId} title={`${v.name} — v${v.version}`}>
                  <Chip
                    label={`${(v.type || '').replace(/_/g, '-')} v${v.version}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Tooltip>
              ))}
              {app.versions.length > 4 && (
                <Chip label={`+${app.versions.length - 4}`} size="small" sx={{ fontSize: '0.7rem' }} />
              )}
            </Stack>
          )}

          {app.tags && app.tags.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 'auto' }} flexWrap="wrap" useFlexGap>
              {app.tags.slice(0, 3).map((t) => (
                <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
            <TimeIcon sx={{ fontSize: 14 }} color="disabled" />
            <Typography variant="caption" color="text.secondary">
              Updated {formatRelative(app.latestScriptUpdate)}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const AppsOverviewPage = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [onlyWithScripts, setOnlyWithScripts] = useState(false);
  const [onlyWithClients, setOnlyWithClients] = useState(false);
  const [sort, setSort] = useState('default');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    appRegistryService
      .getOverview()
      .then((res) => {
        if (!cancelled) setApps(res.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const kinds = useMemo(() => {
    const set = new Set();
    apps.forEach((a) => a.kind && set.add(a.kind));
    return Array.from(set).sort();
  }, [apps]);

  const filtered = useMemo(() => {
    let list = [...apps];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          (a.name || '').toLowerCase().includes(q) ||
          (a.configKey || '').toLowerCase().includes(q) ||
          (a.displayName || '').toLowerCase().includes(q) ||
          (a.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    if (kind !== 'all') list = list.filter((a) => a.kind === kind);
    if (onlyWithScripts) list = list.filter((a) => a.scriptCount > 0);
    if (onlyWithClients) list = list.filter((a) => a.clientCount > 0);

    switch (sort) {
      case 'name':
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'clients':
        list.sort((a, b) => (b.clientCount || 0) - (a.clientCount || 0));
        break;
      case 'scripts':
        list.sort((a, b) => (b.scriptCount || 0) - (a.scriptCount || 0));
        break;
      case 'updated':
        list.sort((a, b) => {
          const at = a.latestScriptUpdate ? new Date(a.latestScriptUpdate).getTime() : 0;
          const bt = b.latestScriptUpdate ? new Date(b.latestScriptUpdate).getTime() : 0;
          return bt - at;
        });
        break;
      default:
        // clients desc, then scripts desc
        list.sort((a, b) => {
          const d = (b.clientCount || 0) - (a.clientCount || 0);
          if (d !== 0) return d;
          return (b.scriptCount || 0) - (a.scriptCount || 0);
        });
    }
    return list;
  }, [apps, search, kind, onlyWithScripts, onlyWithClients, sort]);

  if (loading && apps.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Failed to load apps overview: {error}</Alert>
      </Container>
    );
  }

  const totalClients = apps.reduce((s, a) => s + (a.clientCount || 0), 0);
  const totalScripts = apps.reduce((s, a) => s + (a.scriptCount || 0), 0);
  const appsWithScripts = apps.filter((a) => a.scriptCount > 0).length;

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <AppsIcon fontSize="large" color="primary" />
          <Typography variant="h4" fontWeight={700}>Apps</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {apps.length} apps · {appsWithScripts} with scripts · {totalScripts} scripts · {totalClients} app/client pairings
        </Typography>
      </Box>

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search apps..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={kind} label="Category" onChange={(e) => setKind(e.target.value)}>
            <MenuItem value="all">All categories</MenuItem>
            {kinds.map((k) => (
              <MenuItem key={k} value={k}>{k}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Sort</InputLabel>
          <Select value={sort} label="Sort" onChange={(e) => setSort(e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={onlyWithScripts} onChange={(e) => setOnlyWithScripts(e.target.checked)} />}
          label="Has scripts"
        />
        <FormControlLabel
          control={<Switch checked={onlyWithClients} onChange={(e) => setOnlyWithClients(e.target.checked)} />}
          label="Has clients"
        />
      </Stack>

      {filtered.length === 0 ? (
        <Alert severity="info">No apps match these filters.</Alert>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((app) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={app.configKey}>
              <AppCard app={app} onClick={() => navigate(`/apps/${app.configKey}`)} />
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 3, color: 'text.secondary' }}>
        <Typography variant="caption">Showing {filtered.length} of {apps.length} apps</Typography>
      </Box>
    </Container>
  );
};

export default AppsOverviewPage;
